// MFA requirement hook - v2
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { parseJwtPayload } from "@/hooks/accessRoleUtils";
import { useMemberMfaFactors } from "@/hooks/useMemberMfa";
import { useOrganization } from "@/hooks/useOrganization";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { requiresMfaForRoles } from "@/lib/mfa-enforcement";
import { getStoredDeviceToken } from "@/lib/mfaTrustedDevice";
import { withPromiseTimeout } from "@/lib/promise-timeout";
import { shouldRetryError } from "@/lib/query-client";

interface TrustedDeviceResult {
  trusted?: boolean;
  expiresAt?: string | null;
  mfaSatisfiedAt?: string | null;
}

interface MfaAssuranceLevel {
  current_level?: string | null;
  currentLevel?: string | null;
  next_level?: string | null;
  nextLevel?: string | null;
}

interface MfaSessionState {
  session_found?: boolean;
  session_id?: string | null;
  mfa_satisfied_at?: string | null;
  mfa_locked_until?: string | null;
}

type MfaRequirementStatus = "loading" | "checked";

const MFA_REFRESH_EVENT = "forge:mfa-refresh";
const AUTH_MFA_FACTORS_TIMEOUT_MS = 15_000;
const MFA_CHECK_TIMEOUT_MS = 15_000;

const getCurrentAal = (status: MfaAssuranceLevel | null) =>
  status?.currentLevel?.toLowerCase() ??
  status?.current_level?.toLowerCase() ??
  null;

/**
 * The identity a proof belongs to: the `session_id` and `aal` claims of the
 * access token, never the token bytes.
 *
 * auth-js rotates the access token roughly hourly while a tab is open, stops
 * its refresh ticker while the tab is hidden and refreshes on return once the
 * token is within 90 s of expiry, and relays the other tab's rotation over
 * BroadcastChannel. GoTrue reissues a rotated token for the same session with
 * the same assurance level, so keying the MFA queries on the raw token forced
 * a from-scratch re-proof — and a loader that unmounted the whole page — on
 * every refresh: that is how a batch contribution entry was lost by switching
 * tabs (#6068).
 *
 * The assurance level is itself a claim of the token: a completed challenge
 * issues a new token for the SAME session with a higher level, and
 * `getAuthenticatorAssuranceLevel(jwt)` reads `currentLevel` from that claim
 * (the network call validates the token and reports `nextLevel`). So the
 * identity carries both. A query then starts from nothing exactly when the
 * proof is genuinely new — a new sign-in (new session id), a completed
 * challenge (new level), a different user, organization or device token —
 * and a routine refresh keeps its cached proof. That last property cannot
 * depend on the `forge:mfa-refresh` bump alone: the challenge page mounts
 * under `skipMfaCheck`, so no instance of this hook is listening when it
 * fires, and the remounted hook starts at nonce 0.
 *
 * A token without a `session_id` claim falls back to the token itself, i.e.
 * the old behaviour; a token with a session but no `aal` claim gets the
 * identity `<session>:unknown`, so a later token that does carry the level
 * starts a fresh proof.
 */
function sessionIdentityOf(accessToken: string | null): string | null {
  if (!accessToken) return null;
  const claims = parseJwtPayload(accessToken);
  const sessionId = claims?.session_id;
  const level = claims?.aal;
  if (typeof sessionId !== "string" || sessionId.length === 0) return accessToken;
  return `${sessionId}:${typeof level === "string" && level.length > 0 ? level : "unknown"}`;
}

/**
 * Split a `sessionIdentityOf` result back into its session and level parts.
 * The opaque-token fallback (no session claim) never contains a `:` — JWTs
 * are dot-separated — so it always parses to `null` here, correctly opting
 * out of the upgrade-seeding path below rather than being misread.
 */
function parseSessionIdentity(identity: string | null): { sessionId: string; level: string } | null {
  if (!identity) return null;
  const sep = identity.lastIndexOf(":");
  if (sep === -1) return null;
  return { sessionId: identity.slice(0, sep), level: identity.slice(sep + 1) };
}

const MFA_LEVEL_RANK: Record<string, number> = { unknown: 0, aal1: 1, aal2: 2 };
const mfaLevelRank = (level: string) => MFA_LEVEL_RANK[level] ?? 0;

/**
 * Retry a failed check only while there is no answer yet, and then only as
 * the client's own retry policy allows. Once a proof is cached, a failing
 * re-check must surface immediately: the production `QueryClient` retries a
 * timeout three times with backoff, which would keep a cached "trusted"
 * answer satisfying the gate for about a minute instead of the 15 s ceiling
 * — and unbounded if a retry pauses offline.
 */
function retryOnlyForFirstProof(queryClient: ReturnType<typeof useQueryClient>, key: readonly unknown[]) {
  const clientPolicy = queryClient.getDefaultOptions().queries?.retry;
  return (failureCount: number, error: Error): boolean => {
    if (queryClient.getQueryState(key)?.data !== undefined) return false;
    if (typeof clientPolicy === "function") return clientPolicy(failureCount, error);
    if (typeof clientPolicy === "number") return failureCount < clientPolicy;
    if (typeof clientPolicy === "boolean") return clientPolicy;
    return failureCount < 3 && shouldRetryError(error);
  };
}

/**
 * Every MFA query key has the same head: name, session identity, user id.
 * The user id is redundant with the session id for a genuine Supabase token
 * but keeps the fallback (opaque token) shape safe too: a different user
 * signing in on the same mount — auth-js saves the new session and emits
 * SIGNED_IN with no SIGNED_OUT in between, and relays it to other tabs — can
 * never inherit the previous user's proof.
 */
function mfaQueryKey(
  name: string,
  sessionIdentity: string | null,
  userId: string | null,
  ...rest: unknown[]
) {
  return [name, sessionIdentity, userId, ...rest] as const;
}

export function useMfaRequirement() {
  const { session, isLoading: authLoading } = useAuth();
  const sessionAccessToken = session?.access_token ?? null;
  const sessionUserId = session?.user?.id ?? null;
  const sessionIdentity = useMemo(() => sessionIdentityOf(sessionAccessToken), [sessionAccessToken]);
  const queryClient = useQueryClient();
  // Declared here (rather than beside the effect that writes it, near the
  // bottom) because reading it feeds `initialData` below, which must run
  // during render, on the very first render of a new query key — an effect
  // runs after that render commits, which is too late to stop it from
  // computing "loading" (see the #6081 seeding block below for why).
  const lastSeenSessionRef = useRef<{ token: string | null; identity: string | null } | null>(null);
  // Read fresh each render (cheap localStorage read); re-reads after
  // trust/revoke. Hoisted above the trusted-device-bypass section below so
  // the #6081 seeding block can use it before `trustedDeviceKey` exists yet.
  const storedDevice = getStoredDeviceToken();
  const lastGateRef = useRef<{ context: string; granted: boolean } | null>(null);
  const backgroundUpgradeRef = useRef<string | null>(null);

  // A same-session rotation re-proves the new token in the background (see
  // the effect below). If the network drops between the rotation and that
  // re-proof, TanStack parks the refetch as "paused" without ever running the
  // query function, so no timeout starts and the cached answer would stand
  // indefinitely — a revoked trusted device, or a stale session state, would
  // keep satisfying the gate until connectivity returned. The original hook
  // failed closed here because the rotation re-keyed the query into an empty
  // entry; this keeps that property: a rotation re-proof that cannot run is
  // treated as not yet proven. A paused FOCUS refetch (no rotation) keeps the
  // cached answer, as the original did.
  const settledAt = (query: { dataUpdatedAt: number; errorUpdatedAt: number }) =>
    Math.max(query.dataUpdatedAt, query.errorUpdatedAt);
  const rotationReproofRef = useRef<{ assurance: number; device: number; sessionState: number } | null>(null);
  // Read the settle timestamps and `fetchStatus` on every render, before the
  // snapshot check: TanStack re-renders a component only for result
  // properties the previous render actually read, so a short-circuit that
  // skipped them until a rotation snapshot existed would never be told about
  // the transition to "paused".
  const reproofPaused = (
    query: { dataUpdatedAt: number; errorUpdatedAt: number; fetchStatus: string },
    snapshot: number | undefined,
  ) => {
    const settled = settledAt(query);
    const paused = query.fetchStatus === "paused";
    return snapshot !== undefined && settled <= snapshot && paused;
  };
  const { organizationRole, organizationId, isLoading: orgLoading } = useOrganization();
  const { roles, isLoading: rolesLoading } = useUserRoles();
  const { settings } = useOrgSettings();
  const {
    data: mfaFactors = [],
    isLoading: factorsLoading,
    isError: factorsError,
  } = useMemberMfaFactors();

  const requiresMfa = useMemo(
    () =>
      requiresMfaForRoles(
        roles,
        organizationRole,
        settings,
        session?.user?.user_metadata as Record<string, unknown> | undefined,
        session?.user?.id,
      ),
    [organizationRole, roles, session?.user?.id, session?.user?.user_metadata, settings],
  );

  const enabledFactors = useMemo(
    () => mfaFactors.filter((factor) => factor.status === "verified"),
    [mfaFactors],
  );


  // Enrollment is user/session scoped, independent of the token's AAL. Keep
  // its last answer while a token is rechecked, but never expose it during
  // the first render for another user/session/org (before effects can reset).
  const factorClaims = parseJwtPayload(sessionAccessToken);
  const factorSessionId = factorClaims?.session_id;
  const authFactorsIdentity = JSON.stringify([
    sessionUserId,
    typeof factorSessionId === "string" && factorSessionId.length > 0 ? factorSessionId : sessionAccessToken,
    organizationId,
  ]);
  const [authFactors, setAuthFactors] = useState<{
    identity: string;
    enabled: boolean | null;
    error: boolean;
  } | null>(null);
  const currentAuthFactors = authFactors?.identity === authFactorsIdentity ? authFactors : null;
  const authFactorsEnabled = currentAuthFactors?.enabled ?? null;
  const authFactorsError = currentAuthFactors?.error ?? false;
  const authFactorsLoading = Boolean(sessionAccessToken) && enabledFactors.length === 0 && currentAuthFactors === null;

  useEffect(() => {
    let isMounted = true;

    const loadAuthFactors = async () => {
      if (!sessionAccessToken) {
        setAuthFactors(null);
        return;
      }
      if (enabledFactors.length > 0) {
        setAuthFactors({ identity: authFactorsIdentity, enabled: true, error: false });
        return;
      }
      // Preserve only a successful answer from this identity during recheck.
      setAuthFactors((previous) =>
        previous?.identity === authFactorsIdentity && !previous.error ? previous : null,
      );
      try {
        const { data, error } = await withPromiseTimeout(
          supabase.auth.mfa.listFactors(),
          AUTH_MFA_FACTORS_TIMEOUT_MS,
          "Timed out while loading MFA factors",
        );
        if (!isMounted) return;
        if (error) throw error;
        setAuthFactors({
          identity: authFactorsIdentity,
          enabled: Boolean(data?.all?.some((factor) => factor.status === "verified")),
          error: false,
        });
      } catch (err) {
        console.error("Failed to check auth factors:", err);
        if (isMounted) setAuthFactors({ identity: authFactorsIdentity, enabled: null, error: true });
      }
    };

    void loadAuthFactors();
    return () => { isMounted = false; };
  }, [enabledFactors.length, sessionAccessToken, authFactorsIdentity]);

  // Enrollment check is complete when factor queries have resolved AND at least
  // one result path provided data.  When there is no session token the queries
  // will be disabled / return empty — treat that as "complete" so the hook does
  // not get stuck in a loading state that blocks the auth redirect.
  const noSessionToken = !sessionAccessToken;
  const enrollmentCheckComplete =
    !factorsLoading &&
    !authFactorsLoading &&
    (noSessionToken ||
      enabledFactors.length > 0 ||
      authFactorsEnabled !== null ||
      factorsError ||
      authFactorsError);

  // If we can't reliably determine enrollment (e.g., transient auth restoration / network errors),
  // fail-closed by requiring a challenge rather than forcing setup.
  const enrollmentUnknown = Boolean(
    factorsError ||
      authFactorsError ||
      (!enabledFactors.length && authFactorsEnabled === null),
  );
  const hasEnrollment = enabledFactors.length > 0 || authFactorsEnabled === true;

  const [refreshNonce, setRefreshNonce] = useState(0);

  // Allow other parts of the app (e.g., /mfa/challenge) to force a refresh of MFA status.
  // This prevents stale checks from causing a redirect loop when session claims update.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = () => {
      setRefreshNonce((v) => v + 1);
    };

    window.addEventListener(MFA_REFRESH_EVENT, handler);
    return () => window.removeEventListener(MFA_REFRESH_EVENT, handler);
  }, []);


  const shouldCheckSession =
    requiresMfa &&
    Boolean(sessionAccessToken) &&
    enrollmentCheckComplete &&
    (hasEnrollment || enrollmentUnknown);

  // A mounted, satisfied gate may carry its verified device proof through
  // an aal1 → aal2 upgrade. Never seed a cached proof from a different gate
  // context or a prior denied/error state; the fresh token must be re-proved.
  const rememberWindowDays = settings?.mfaRememberDeviceWindowDays ?? 30;
  const reauthPeriodHours = settings?.mfaReauthPeriodHours ?? 0;
  const gateContext = JSON.stringify([
    sessionUserId, organizationId, refreshNonce, storedDevice?.deviceId,
    storedDevice?.token, rememberWindowDays, reauthPeriodHours, requiresMfa,
  ]);
  const upgradeContext = JSON.stringify([sessionIdentity, gateContext]);
  const previousSession = lastSeenSessionRef.current;
  const previousSessionIdentityParsed = parseSessionIdentity(previousSession?.identity ?? null);
  const currentSessionIdentityParsed = parseSessionIdentity(sessionIdentity);
  const isSameSessionUpgradeToAal2 =
    !!previousSession &&
    previousSession.identity !== sessionIdentity &&
    !!previousSessionIdentityParsed &&
    !!currentSessionIdentityParsed &&
    previousSessionIdentityParsed.sessionId === currentSessionIdentityParsed.sessionId &&
    currentSessionIdentityParsed.level === "aal2" &&
    mfaLevelRank(currentSessionIdentityParsed.level) > mfaLevelRank(previousSessionIdentityParsed.level);
  const priorTrustedDeviceData = isSameSessionUpgradeToAal2
    ? queryClient.getQueryData<TrustedDeviceResult>(
        mfaQueryKey(
          "mfa-trusted-device",
          previousSession!.identity,
          sessionUserId,
          organizationId,
          refreshNonce,
          storedDevice?.deviceId,
          storedDevice?.token,
        ),
      )
    : undefined;
  const isValidatedBackgroundUpgrade =
    isSameSessionUpgradeToAal2 &&
    previousSessionIdentityParsed?.level === "aal1" &&
    lastGateRef.current?.context === gateContext &&
    lastGateRef.current.granted &&
    priorTrustedDeviceData?.trusted === true;
  const backgroundUpgrade = isValidatedBackgroundUpgrade || backgroundUpgradeRef.current === upgradeContext;
  const priorSessionStatusData = isValidatedBackgroundUpgrade
    ? queryClient.getQueryData<MfaAssuranceLevel>(
        mfaQueryKey("mfa-assurance-level", previousSession!.identity, sessionUserId, refreshNonce),
      )
    : undefined;

  const sessionStatusKey = mfaQueryKey("mfa-assurance-level", sessionIdentity, sessionUserId, refreshNonce);
  const sessionStatusQuery = useQuery({
    queryKey: sessionStatusKey,
    enabled: shouldCheckSession,
    staleTime: 15_000,
    gcTime: 60_000,
    // Seeds the brand-new key synchronously, during render — an effect runs
    // one tick too late to stop ProtectedRoute from computing `isLoading`
    // for at least one committed render, which is enough to unmount the
    // page (React Query doesn't retroactively un-render a commit that
    // already happened). See the block above for the narrow condition.
    initialData: priorSessionStatusData,
    queryFn: async (): Promise<MfaAssuranceLevel | null> => {
      if (!sessionAccessToken) {
        return null;
      }

      try {
        // Bounded so a hanging background re-proof cannot keep the previous
        // answer on screen past this ceiling: a timeout lands in the catch
        // below and returns null, which the hook treats as "unknown, challenge".
        const { data, error } = await withPromiseTimeout(
          supabase.auth.mfa.getAuthenticatorAssuranceLevel(sessionAccessToken),
          MFA_CHECK_TIMEOUT_MS,
          "Timed out while checking the MFA assurance level",
        );

        if (error || !data) {
          return null;
        }

        return data as MfaAssuranceLevel;
      } catch (err) {
        console.error("Failed to get MFA assurance level:", err);
        return null;
      }
    },
  });

  const sessionStatus = sessionStatusQuery.data ?? null;
  // Block only on the FIRST proof of a session. TanStack re-runs this query on every window
  // focus once it is 15 s stale — i.e. on every return to the tab — and a
  // background refetch keeps the last proven level in `data`. Keying these
  // flags off `isFetching` flipped `status` back to "loading" for the length
  // of the round-trip, and `ProtectedRoute` answered by replacing the page with
  // a loader, destroying its state (#6068). `isLoading` is pending AND
  // fetching: true for the first proof, false for a refetch, and — unlike
  // `isPending` alone — false for a disabled query, which would otherwise wedge
  // the guard. A result already in hand counts as checked while it is
  // re-proved in the background (bounded by MFA_CHECK_TIMEOUT_MS); the
  // fail-closed semantics are unchanged, a null result still means "unknown,
  // challenge", and a session or user change starts from nothing.
  const isCheckingSession = sessionStatusQuery.isLoading;
  const assuranceReproofPaused = reproofPaused(sessionStatusQuery, rotationReproofRef.current?.assurance);
  const sessionCheckState: "idle" | "checking" | "checked" | "skipped" = !shouldCheckSession
    ? (enrollmentCheckComplete ? "skipped" : "idle")
    : assuranceReproofPaused
      ? "checking"
      : sessionStatusQuery.data !== undefined || sessionStatusQuery.isError
        ? "checked"
        : sessionStatusQuery.isFetching
          ? "checking"
          : "idle";

  const currentAal = getCurrentAal(sessionStatus);
  const sessionFound = sessionStatus !== null;

  // Trusted-device bypass (PROMPT-04 / Audit §1.7): when the org enables
  // remember-device (mfaRememberDeviceWindowDays > 0) and this browser holds a
  // valid token, a server-verified trusted device can stand in for re-verifying
  // the second factor — i.e. skip the TOTP challenge for the trust window.
  // Only consulted when the session is NOT already AAL2 (no point otherwise).
  const shouldCheckTrustedDevice =
    requiresMfa &&
    rememberWindowDays > 0 &&
    Boolean(storedDevice) &&
    Boolean(organizationId) &&
    sessionCheckState === "checked" &&
    sessionFound &&
    currentAal !== "aal2";

  const trustedDeviceKey = mfaQueryKey(
    "mfa-trusted-device",
    sessionIdentity,
    sessionUserId,
    organizationId,
    refreshNonce,
    storedDevice?.deviceId,
    storedDevice?.token,
  );
  const trustedDeviceQuery = useQuery({
    queryKey: trustedDeviceKey,
    enabled: shouldCheckTrustedDevice,
    staleTime: 60_000,
    gcTime: 120_000,
    retry: retryOnlyForFirstProof(queryClient, trustedDeviceKey),
    // See the #6081 block above sessionStatusKey — same reasoning, same key.
    initialData: isValidatedBackgroundUpgrade ? priorTrustedDeviceData : undefined,
    queryFn: async (): Promise<TrustedDeviceResult> => {
      if (!sessionAccessToken) {
        return { trusted: false };
      }

      const { data, error } = await withPromiseTimeout(
        supabase.functions.invoke<TrustedDeviceResult>("mfa-trust-device", {
          body: {
            action: "verify",
            deviceId: storedDevice!.deviceId,
            token: storedDevice!.token,
            organizationId,
          },
          headers: {
            Authorization: `Bearer ${sessionAccessToken}`,
          },
        }),
        MFA_CHECK_TIMEOUT_MS,
        "Timed out while verifying the trusted device",
      );
      if (error) throw error;
      return data ?? { trusted: false };
    },
  });

  const trustedDeviceChecked =
    !shouldCheckTrustedDevice || trustedDeviceQuery.data !== undefined || trustedDeviceQuery.isError;
  const deviceReproofPaused = reproofPaused(trustedDeviceQuery, rotationReproofRef.current?.device);
  const trustedDeviceSatisfied =
    shouldCheckTrustedDevice &&
    !trustedDeviceQuery.isError &&
    trustedDeviceQuery.data?.trusted === true &&
    !deviceReproofPaused;

  const hasAal2Session = sessionCheckState === "checked" && sessionFound && currentAal === "aal2";
  const hasTrustedDeviceSession = sessionCheckState === "checked" && sessionFound && trustedDeviceSatisfied;
  const shouldCheckReauthWindow =
    reauthPeriodHours > 0 &&
    sessionCheckState === "checked" &&
    sessionFound &&
    // Begin this proof alongside the assurance/device proofs on upgrade,
    // so retaining a trusted page cannot extend the 15-second ceiling.
    (currentAal === "aal2" || backgroundUpgrade);

  const mfaSessionStateKey = mfaQueryKey(
    "mfa-session-state",
    sessionIdentity,
    sessionUserId,
    organizationId,
    refreshNonce,
    reauthPeriodHours,
  );
  const mfaSessionStateQuery = useQuery({
    queryKey: mfaSessionStateKey,
    enabled: shouldCheckReauthWindow,
    staleTime: 15_000,
    gcTime: 60_000,
    // The upgrade temporarily retains a grant while this FIRST window proof
    // runs. Do not let first-proof retries prolong that grant past its timeout.
    retry: backgroundUpgrade ? false : retryOnlyForFirstProof(queryClient, mfaSessionStateKey),
    queryFn: async (): Promise<MfaSessionState | null> => {
      if (!sessionAccessToken) {
        return null;
      }

      const { data, error } = await withPromiseTimeout(
        supabase.functions.invoke<MfaSessionState>("mfa-session-state", {
          body: { action: "status" },
          headers: {
            Authorization: `Bearer ${sessionAccessToken}`,
          },
        }),
        MFA_CHECK_TIMEOUT_MS,
        "Timed out while loading the MFA session state",
      );

      if (error) {
        throw error;
      }
      return data ?? { session_found: false, session_id: null, mfa_satisfied_at: null, mfa_locked_until: null };
    },
  });

  // A token refresh inside one session leaves every key unchanged, so the
  // cached proof stays on screen and the page stays mounted — but the new
  // token is re-proved in the background right away rather than at the next
  // focus refetch. A changed session identity needs nothing here: its keys
  // are new and start from nothing. This effect sits after the three queries
  // so their observers have already adopted the queryFn closures that carry
  // the new token; `isLoading` is false during the refetch, so the route
  // guard never sees it.
  //
  // The in-flight request is cancelled first. `invalidateQueries` alone only
  // restarts a fetch that already has data; a FIRST proof still in flight
  // would keep running with the old token, and if that token expired
  // mid-request its null/error result would settle the query and send a
  // perfectly valid replacement session to the challenge page.
  useEffect(() => {
    const previous = lastSeenSessionRef.current;
    lastSeenSessionRef.current = { token: sessionAccessToken, identity: sessionIdentity };
    if (!previous || !sessionAccessToken) return;
    if (previous.token === sessionAccessToken) return;

    if (previous.identity !== sessionIdentity) {
      backgroundUpgradeRef.current = isValidatedBackgroundUpgrade ? upgradeContext : null;
      rotationReproofRef.current = null;
      // An identity change normally starts from nothing by design (see
      // sessionIdentityOf's doc comment). The one carve-out — a same-session
      // upgrade to aal2 while this tab was already trusted-device-satisfied
      // — has already been seeded synchronously via `initialData` above,
      // during the render that first observed the new identity; an effect
      // runs one render too late to prevent that render's `isLoading` from
      // reaching a commit (#6081). All that's left to do here is kick the
      // seeded queries into actually re-proving instead of sitting fresh
      // (their seeded `initialData` counts as "just fetched", so neither
      // would refetch on its own before staleTime).
      if (isValidatedBackgroundUpgrade) {
        rotationReproofRef.current = {
          assurance: settledAt(sessionStatusQuery),
          device: settledAt(trustedDeviceQuery),
          sessionState: settledAt(mfaSessionStateQuery),
        };
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: sessionStatusKey }),
          queryClient.invalidateQueries({ queryKey: trustedDeviceKey }),
        ]);
      }
      return;
    }

    rotationReproofRef.current = {
      assurance: settledAt(sessionStatusQuery),
      device: settledAt(trustedDeviceQuery),
      sessionState: settledAt(mfaSessionStateQuery),
    };
    void (async () => {
      const reproofKeys = ["mfa-assurance-level", "mfa-trusted-device", "mfa-session-state"];
      // Both phases fan out across all three keys before either is awaited.
      // `invalidateQueries` awaits the refetch it triggers, so cancelling and
      // invalidating one key at a time meant the trusted-device and
      // session-state proofs kept serving their stale cached values until the
      // assurance request settled. An assurance reproof returning near its
      // 15s timeout followed by a hung trusted-device check left a cached
      // `trusted: true` satisfying the MFA gate for nearly 30s, twice the
      // intended ceiling (Codex P2, PR #6082).
      //
      // Cancel-before-invalidate is still ordered, and for the same reason as
      // before: invalidate alone only restarts a query that already has data,
      // so a FIRST proof still in flight would keep running with the old token.
      await Promise.all(reproofKeys.map((name) => queryClient.cancelQueries({ queryKey: [name] })));
      await Promise.all(reproofKeys.map((name) => queryClient.invalidateQueries({ queryKey: [name] })));
    })();
    // The three query results are read for their settle timestamps only; the
    // effect must run exactly once per token change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, sessionAccessToken, sessionIdentity]);

  const mfaSessionState = mfaSessionStateQuery.data ?? null;
  const sessionStateReproofPaused = reproofPaused(mfaSessionStateQuery, rotationReproofRef.current?.sessionState);
  const mfaSessionChecked =
    !shouldCheckReauthWindow || mfaSessionStateQuery.data !== undefined || mfaSessionStateQuery.isError;
  // A fresh AAL2 answer can arrive before its reauth-window answer. During
  // that first pending check the already verified device grant remains valid.
  // A paused/error/completed state never takes this continuity path.
  const upgradeDeviceSatisfied =
    backgroundUpgrade &&
    sessionCheckState === "checked" &&
    sessionFound &&
    !mfaSessionChecked &&
    !sessionStateReproofPaused &&
    !trustedDeviceQuery.isError &&
    trustedDeviceQuery.data?.trusted === true &&
    !deviceReproofPaused;
  const mfaLockedUntil = mfaSessionState?.mfa_locked_until ?? null;
  const mfaLockActive =
    !!mfaLockedUntil &&
    Number.isFinite(Date.parse(mfaLockedUntil)) &&
    Date.parse(mfaLockedUntil) > Date.now();
  const mfaReauthExpired = (() => {
    if (reauthPeriodHours <= 0) return false;
    // A completed server lock outranks device continuity even while the
    // upgrade assurance proof is pending, or if that proof later fails.
    if (mfaLockActive) return true;

    const reauthWindowMs = reauthPeriodHours * 60 * 60 * 1000;

    if (trustedDeviceSatisfied || upgradeDeviceSatisfied) {
      const trustedSatisfiedAtRaw = trustedDeviceQuery.data?.mfaSatisfiedAt;
      if (!trustedSatisfiedAtRaw) return true;
      const trustedSatisfiedAt = Date.parse(trustedSatisfiedAtRaw);
      if (!Number.isFinite(trustedSatisfiedAt)) return true;
      return Date.now() - trustedSatisfiedAt >= reauthWindowMs;
    }

    if (!shouldCheckReauthWindow || !mfaSessionChecked) return false;
    // A rotation re-proof parked offline cannot confirm the window is still
    // open; require the challenge until it runs.
    if (sessionStateReproofPaused) return true;
    // Avoid hard-locking users if the state service is transiently unavailable
    // and there is no answer at all. With a cached answer, a failed re-check
    // (a timeout, a 5xx) must not unmask an expired window: the last known
    // satisfaction time plus the current clock still decides.
    if (mfaSessionStateQuery.isError && mfaSessionState === null) return false;
    const satisfiedAtRaw = mfaSessionState?.mfa_satisfied_at ?? null;
    if (!mfaSessionState?.session_found && !satisfiedAtRaw) return true;

    if (!satisfiedAtRaw) return true;
    const satisfiedAt = Date.parse(satisfiedAtRaw);
    if (!Number.isFinite(satisfiedAt)) return true;

    return Date.now() - satisfiedAt >= reauthWindowMs;
  })();

  // MFA is satisfied when server assurance confirms AAL2, OR when a server-verified
  // trusted device stands in for it (opt-in per org via mfaRememberDeviceWindowDays).
  // Trusted-device verification is org-scoped and returns a fresh satisfaction
  // timestamp, so it never refreshes the global device_sessions reauth state.
  const mfaSatisfied = hasAal2Session || hasTrustedDeviceSession || upgradeDeviceSatisfied;

  // MFA enforcement grace window (W6-4 / D5): when an org configures a grace
  // period, staff who haven't enrolled are NOT force-redirected to /mfa/setup
  // until the window after enforcement was turned on elapses. mfaEnforcedAt is
  // stamped by SettingsSecurity when enforcement flips on; a missing anchor means
  // "no grace" — enforce immediately (fail-closed). Grace only suppresses setup;
  // already-enrolled users are still challenged (needsChallenge) as normal.
  const graceDays = settings?.mfaGracePeriodDays ?? 0;
  const enforcedAtRaw = settings?.mfaEnforcedAt ?? null;
  const withinMfaGracePeriod =
    graceDays > 0 &&
    !!enforcedAtRaw &&
    Number.isFinite(Date.parse(enforcedAtRaw)) &&
    Date.now() - Date.parse(enforcedAtRaw) < graceDays * 24 * 60 * 60 * 1000;

  const needsSetup =
    enrollmentCheckComplete &&
    !enrollmentUnknown &&
    requiresMfa &&
    !hasEnrollment &&
    !withinMfaGracePeriod;

  // Require a challenge whenever the completed session check cannot prove MFA
  // satisfaction. A missing assurance response is an unknown state and must
  // remain blocked rather than allowing a protected route through.
  const needsChallenge =
    requiresMfa &&
    (hasEnrollment || enrollmentUnknown) &&
    sessionCheckState === "checked" &&
    (!sessionFound || !mfaSatisfied || mfaReauthExpired);
  const reauthRequired = needsChallenge && mfaReauthExpired;

  const rolesResolved = !authLoading && !orgLoading && !rolesLoading;
  const isLoading =
    !rolesResolved ||
    (requiresMfa &&
      (factorsLoading ||
        authFactorsLoading ||
        isCheckingSession ||
        (!mfaSessionChecked && !upgradeDeviceSatisfied) ||
        !trustedDeviceChecked ||
        (!noSessionToken && (sessionCheckState === "idle" || sessionCheckState === "checking"))));

  const status: MfaRequirementStatus = isLoading ? "loading" : "checked";
  const checked = status === "checked";

  useEffect(() => {
    lastGateRef.current = {
      context: gateContext,
      granted: checked && mfaSatisfied && !needsChallenge && !needsSetup,
    };
  });

  return {
    requiresMfa,
    hasEnrollment,
    mfaSatisfied,
    needsSetup,
    needsChallenge,
    reauthRequired,
    mfaLockedUntil: mfaLockActive ? mfaLockedUntil : null,
    sessionFound,
    isLoading,
    status,
    checked,
    enrollmentUnknown,
    enrollmentCheckComplete,
    sessionCheckState,
  };
}
